import { NoOpCacheService, type CacheService } from './no-op-cache.service'
import { reauth } from './reauth.service'

export const REAUTH_HEADER = 'X-Reauth-Token'

interface ApiResponse {
    json: any | null
    text: string | null
    buffer: any
    statusCode: number | null
    message: any | null
}

export class Request {
    constructor(private cache?: CacheService) {}

    // mesmo critério de BaseRepository.resolvedCache — sem injeção, browser usa a cache global, backend não guarda nada
    get resolvedCache(): CacheService {
        return this.cache || (typeof window !== 'undefined' ? window.api.cache : new NoOpCacheService())
    }

    async get(url: string, token?: string): Promise<ApiResponse> {
        if (!token) {
            const params = new URL(url).searchParams
            const parToken = params.get('token')
            if (parToken) token = parToken
        }
        let headers = {}
        //headers['content-type'] = 'application/json'
        if (token) headers['Authorization'] = 'bearer ' + token
        const send = (extra: Record<string, string> = {}) => fetch(url, { headers: { ...headers, ...extra } })
        const result = await this.evaluateResponse(await send())
        return await this.retryWithReauth(result, token, send)
    }

    async delete(url: string, data?: object | Array<object> | any, token?: string): Promise<ApiResponse> {
        this.resolvedCache.clear(url.split('/')[2])
        return this._send(url, 'DELETE', data, token)
    }

    async post(url: string, data: object | Array<object> | any, token?: string): Promise<ApiResponse> {
        this.resolvedCache.clear(url.split('/')[2])
        return this._send(url, 'POST', data, token)
    }

    async patch(url: string, data: object | Array<object> | any, token?: string): Promise<ApiResponse> {
        this.resolvedCache.clear(url.split('/')[2])
        return this._send(url, 'PATCH', data, token)
    }

    async put(url: string, data: object | Array<object> | any, token?: string): Promise<ApiResponse> {
        this.resolvedCache.clear(url.split('/')[2])
        return this._send(url, 'PUT', data, token)
    }

    private async _send(url: string, method: string, data: object | Array<object> | any, token?: string): Promise<ApiResponse> {
        if (!token) {
            const params = new URL(url).searchParams
            const parToken = params.get('token')
            if (parToken) token = parToken
        }

        let headers = {}
        let body = undefined
        if (data) body = JSON.stringify(data)
        headers['content-type'] = 'application/json'
        if (token) headers['Authorization'] = 'bearer ' + token
        try {
            const send = (extra: Record<string, string> = {}) =>
                fetch(url, {
                    headers: { ...headers, ...extra },
                    method,
                    body,
                })
            const result = await this.evaluateResponse(await send())
            return await this.retryWithReauth(result, token, send)
        } catch (error) {
            console.log(error, data)
        }
    }

    // Ação sensível: o backend recusou pedindo confirmação de senha (ver ReauthGuard em _common/auth).
    // Pede a senha, troca por um token de vida curta e repete a chamada uma única vez — o retry não
    // pode recorrer, senão uma senha errada viraria laço. Se o usuário cancelar, devolve o 403 original
    // e quem chamou trata como qualquer outro erro.
    private async retryWithReauth(
        result: any,
        token: string,
        send: (extra?: Record<string, string>) => Promise<Response>,
    ) {
        if (!reauth.isChallenge(result)) return result
        const reauthToken = await reauth.resolve(reauth.challengeOf(result), token)
        if (!reauthToken) return result
        return await this.evaluateResponse(await send({ [REAUTH_HEADER]: reauthToken }))
    }

    async evaluateResponse(response: Response) {
        const contentType = response.headers.get('content-type')
        let buffer
        let json = null
        let text = null
        let message = null
        let statusCode = null
        try {
            if (contentType.includes('text/')) {
                text = await response.text()
            } else if (contentType.includes('/pdf')) {
                buffer = await response.arrayBuffer()
            } else {
                json = await response.json()
            }
            if (json.statusCode && json.message) message = json.message
        } catch (error) {}
        try {
            statusCode = response.status
        } catch (error) {
            statusCode = 999
            message = 'Unknown error'
        }
        const result = { json, text, buffer, statusCode, message }
        if (String(statusCode).substring(0, 1) !== '2') console.error('Request Error', result)
        return result
    }
}
